import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Row, Col, Typography, Card, Form, Button, Input,
  notification,
} from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import { getUserMail, getUserType } from '../../../../../utils/users';
import UploadFiles from '../../../serviceComponents/uploadFiles/UploadFiles';
import { isItEstimateOrService } from '../../../../../utils/service';
import {
  post, get, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../../../dataSource/requests';
import './Summary.scss';
import { backendResponseCodes } from '../../../../../utils/responseMessages';

const Summary = (props): JSX.Element => {
  const [formInstance] = Form.useForm();
  const navigate = useNavigate();
  const userType = getUserType();

  const uploadFilesReference = useRef(null);
  const [valueAttachmentSizeCooperated, setValueAttachmentSizeCooperated] = useState(null);

  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const titleOrcamento = useRef(false);

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const {
    serviceRequestParameters, nextPage, previousPage,
  } = props;

  /**
   * Identificar qual o e-mail do usuário logado e que está fazendo a solicitação da operação
   * @returns se o usuário for do perfil cooperado o e-mail estará validado e será exibido
   */
  const userMailCooperative = (): JSX.Element => {
    const targetMail = getUserMail() ? getUserMail()?.replaceAll('"', '') : '-';

    return (
      <div>
        <div>
          <span>{`Email: ${userType === 'COOPERATIVE' ? targetMail : '-'}`}</span>
        </div>
        <div>
          <span style={{ fontSize: '.7rem' }}>(caso esteja incorreto, informar e-mail válido no campo observação)</span>
        </div>
      </div>
    );
  };

  const getLabel = (idCode: string, value: any): string => {
    switch (idCode) {
      case 'POS_VENDA_ASSISTENCIA_TECNICA_MUNICIPIO':
        return 'Qual o município em que se encontra o equipamento';

      case 'POS_VENDA_ASSISTENCIA_TECNICA_MODELO':
        return 'Modelo do equipamento';

      case 'POS_VENDA_ASSISTENCIA_TECNICA_MOTIVO_SOLICITACAO':
        return 'Motivo da solicitação';

      case 'POS_VENDA_ASSISTENCIA_TECNICA_TIPO_ORDEM':
        return 'Tipo de ordem';

      case 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO':
        if (value === '') return '';
        return 'Valor unitário';
      case 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_TOTAL':
        if (value === '') return '';
        return 'Valor total';
      case 'POS_VENDA_ASSISTENCIA_TECNICA-CONDICAO_PAGAMENTO':
        if (value === '') return '';
        return 'Condição de pagamento';
      case 'POS_VENDA_ASSISTENCIA_TECNICA-FORMA_PAGAMENTO':
        if (value === '') return '';
        return 'Forma de pagamento';
      case 'POS_VENDA_ASSISTENCIA_TECNICA-OBSERVACOES':
        if (value === '') return '';
        return 'Observações do orçamento';
      default:
        return '';
    }
  };

  const getField = (idCodeParameter: string): any => {
    return serviceRequestParameters
      .serviceRequestFieldInputList
      .find(
        (field) => {
          return field.idCode === idCodeParameter;
        },
      );
  };

  const printValue = (idCode: string, value: any): any => {
    switch (true) {
      case (value === true):
      case (value === 'true'):
        return 'Sim';

      case (value === false):
      case (value === 'false'):
        return 'Não';

      case (value?._isAMomentObject):
        return value.format('DD/MM/YYYY');

      case (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value)):
        return moment(value, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY');

      case (idCode === 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO' && value !== ''):
        return currencyFormatter.format(value);

      case (idCode === 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_TOTAL' && value !== ''):
        return currencyFormatter.format(value);

      default:
        return value;
    }
  };

  const renderField = (idCodeParameter: string): JSX.Element | null => {
    const field = getField(idCodeParameter);

    if (field !== undefined) {
      if (idCodeParameter === 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO' && field.value !== '') {
        titleOrcamento.current = true;
      }

      return (
        <Row
          className="AssistenciaTecnicaSummary__DetailRow"
        >
          <Col
            span={24}
            className="AssistenciaTecnicaSummary__DetailParentColumn"
          >

            {
              idCodeParameter === 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO' && field.value !== ''
              && (
                <>
                  <hr />
                  <p className="AssistenciaTecnicaSummary__SessionTitle">Orçamento</p>
                </>
              )
            }

            {
              titleOrcamento.current === false
              && idCodeParameter === 'POS_VENDA_ASSISTENCIA_TECNICA-OBSERVACOES'
              && field.value !== ''
              && (
                <>
                  <hr />
                  <p className="AssistenciaTecnicaSummary__SessionTitle">Orçamento</p>
                </>
              )
            }

            <Row
              justify="space-between"
              className="AssistenciaTecnicaSummary__DetailChildRow"
            >
              <Col
                className="AssistenciaTecnicaSummary__DetailColumn"
              >
                {getLabel(idCodeParameter, field.value)}
              </Col>
              <Col
                className="AssistenciaTecnicaSummary__DetailSpecificColumn"
              >
                {printValue(idCodeParameter, field.value)}
              </Col>
            </Row>
          </Col>
        </Row>
      );
    }

    return null;
  };

  const mask = (rawValue: string): (string | RegExp)[] => {
    if (rawValue.length > 14) {
      return [
        '(',
        /[1-9]/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ];
    }
    return [
      '(',
      /[1-9]/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  };

  const getAttachmentParamenters = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const requestRef: RequestWrapper = get({
        url: '/system/getAttachmentParamenters',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body.code === 8005):
        // "Attachment Paramenters Got Successfully", 8005
        setValueAttachmentSizeCooperated(
          response?.body.getAttachmentParamentersOutput.attachmentSizeCooperated,
        );
        break;

      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar informações, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    getAttachmentParamenters();
    return () => {
      setLoading(false);
    };
  }, []);

  const onFormFinish = async (values): Promise<void> => {
    setLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let telValue = values.TelInput;
    telValue = !telValue ? '' : telValue.replace(/\D/g, '');

    const data = {
      landFieldsIds: (serviceRequestParameters.talhoesSelecionados
        ? serviceRequestParameters.talhoesSelecionados.map((v) => v.id) : null),
      landId: serviceRequestParameters.selectedProperty.id,
      landOwnerId: serviceRequestParameters.selectedOwner.id,
      serviceId: serviceRequestParameters.selectedService.id,
      cooperativeNotes: values.observacao,
      telephone: telValue,
      serviceTypeId: serviceRequestParameters.selectedType.id,
      haveCtc: serviceRequestParameters.haveCtc,
      ctc: serviceRequestParameters.ctc,
      branchCoopercitrusId: serviceRequestParameters.branchCoopercitrusId,
    };

    if (userType === 'COOPERATIVE') {
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'POS_VENDA_ASSISTENCIA_TECNICA-VALOR_TOTAL',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'POS_VENDA_ASSISTENCIA_TECNICA-CONDICAO_PAGAMENTO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'POS_VENDA_ASSISTENCIA_TECNICA-FORMA_PAGAMENTO',
        value: '',
      });
      serviceRequestParameters.serviceRequestFieldInputList.push({
        idCode: 'POS_VENDA_ASSISTENCIA_TECNICA-OBSERVACOES',
        value: '',
      });
    }

    let targetUrl = '';
    if (serviceRequestParameters.choiceServiceType === 1) {
      targetUrl = '/budget/requestBudgetService';
      data.budgetRequestFieldInputs = serviceRequestParameters.serviceRequestFieldInputList;
    } else {
      targetUrl = '/service/requestService';
      data.serviceRequestFieldInputList = serviceRequestParameters.serviceRequestFieldInputList;
    }

    try {
      const requestRef: RequestWrapper = post({
        url: targetUrl,
        params: data,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5002): {
        // "Service requested successfully", 5002
        flushSync(() => {
          setRequestId(response?.body.createServiceRequestOutput.serviceRequestId);
        });
        const uploadError = !uploadFilesReference?.current?.validateFiles();
        if (!uploadError) {
          try {
            await uploadFilesReference?.current?.handleSubmitFiles();
          } catch (error: unknown) {
            setLoading(false);
            return;
          }

          nextPage({});
        }
      } break;

      case (response?.body?.code === 13001):
      case (response?.body?.code === 5013): {
        // "Budget requested successfully", 13001
        // "Service request edited successfully", 5013
        flushSync(() => {
          setRequestId(response?.body.createBudgetOutput.budgetId);
        });
        const uploadError = !uploadFilesReference?.current?.validateFiles();
        if (!uploadError) {
          try {
            await uploadFilesReference?.current?.handleSubmitFiles();
          } catch (error: unknown) {
            setLoading(false);
            return;
          }

          nextPage({});
        }
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .General.Forbidden
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6007):
        // "Land owner not found", -6007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandOwnerNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6009):
        // "Land owner is not from an activated matriculation", -6009
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandOwnerIsNotFromAnActivatedMatriculation
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6002):
        // "Land not found", -6002
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6008):
        // "Land owner do not matches with land", -6008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandOwnerDoNotMatchesWithLand
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5000):
        // "Service not found", -5000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ServiceNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5017):
        // "Min quantity of unreached hectare", -5017
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.MinQuantityOfUnreachedHectare
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5001):
        // "Service type not found", -5001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ServiceTypeNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5028):
        // "Cooperative notes too large", -5028
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.CooperativeNotesTooLarge
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5024):
        // "Coopercitrus notes too large", -5024
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.CooperCitrusNotesTooLarge
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5023):
        // "Telephone can not be null", -5023
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.TelephoneCanNotBeNull
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5021):
        // "Telephone size is too large", -5021
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.TelephoneSizeTooLarge
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5022):
        // "Telephone size is too small", -5022
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.TelephoneSizeTooSmall
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5030):
        // "mandatory fill ctc", -5030
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.mandatoryFillCtc
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5031):
        // "required branch", -5031
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.requiredBranch
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5003):
        // "Service subtype not found", -5003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ServiceSubtypeNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6004):
        // "Land field is not from informed land", -6004
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandFieldIsNotFromInformedLand
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6017):
        // "Land with different cultures", -6017
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.LandsWithDifferentCultures
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5029):
        // "Problems with depths values", -5029
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ProblemsWithDepthsValues
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6005):
        // "Culture not found", -6005
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.CultureNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5027):
        // "Without weed map you cannot continue", -5027
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ServiceSubtypeNoWeedMap
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5002):
        // "Service type dont match with service", -5002
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.ServiceTypeDontMatchWithService
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -6006):
        // "This culture is not valid for this service", -6006
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Land.ThisCultureIsNotValidForThisService
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5006):
        // "Invalid service type dynamic fields", -5006
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.InvalidServiceTypeDynamicFields
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5005):
        // "Invalid service subtype dynamic fields", -5005
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.InvalidServiceSubtypeDynamicFields
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5032):
        // "Technician not found", -5032
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error
            .Service.TechnicianNotFound
            .description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao solicitar o serviço, por tente novamente',
        });
        break;
    }

    setLoading(false);
  };

  return (
    <div className="AssistenciaTecnicaSummary">
      <Row
        justify="space-between"
        className="AssistenciaTecnicaSummary__TitleRow"
      >
        <Col
          className="AssistenciaTecnicaSummary__TitleCol"
        >
          <Typography.Title
            level={1}
            className="AssistenciaTecnicaSummary__Title"
          >
            Resumo da Solicitação
          </Typography.Title>
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        className="AssistenciaTecnicaSummary__ContentRow"
      >
        <Col
          span={24}
          className="AssistenciaTecnicaSummary__ContentColumn"
        >
          <Card
            className="AssistenciaTecnicaSummary__ContentCard"
          >
            <Form
              scrollToFirstError
              className="AssistenciaTecnicaSummary__Form"
              form={formInstance}
              onFinish={onFormFinish}
              layout="vertical"
              onFinishFailed={
                (errorInfo): void => {
                  console.log('grid summary form finish failed:', errorInfo);
                }
              }
            >
              <Row
                gutter={[24, 24]}
                className="AssistenciaTecnicaSummary__DetailsRow"
              >
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 12 }}
                  className="AssistenciaTecnicaSummary__DetailsColumn"
                >
                  <Row
                    gutter={[24, 24]}
                    className="AssistenciaTecnicaSummary__ServiceTitleRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__ServiceTitleCol"
                    >
                      <Typography.Title
                        level={1}
                        className="AssistenciaTecnicaSummary__ServiceFullTitle"
                      >
                        <span className="AssistenciaTecnicaSummary__ServiceTitle">{'Pós-vendas: '}</span>
                        <span className="AssistenciaTecnicaSummary__ServicesTypeTitle">Assistência Técnica</span>
                      </Typography.Title>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__MatriculaRow"
                  >
                    <Col
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__MatriculaColumn"
                      xs={{ span: 24 }}
                    >
                      {`Matrícula: ${serviceRequestParameters.selectedProperty.matriculation}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__CooperadoRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__CooperadoColumn"
                    >
                      {`Cooperado: ${serviceRequestParameters.selectedOwner.name}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      {`Propriedade: ${serviceRequestParameters.selectedProperty.name}`}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      {
                        userMailCooperative()
                      }
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      <span>{`Possui CTC: ${serviceRequestParameters.haveCtc ? 'Sim' : 'Não'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      <span>{`CTC: ${serviceRequestParameters.ctc ? serviceRequestParameters.ctc : '-'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      <span>{`Filial: ${serviceRequestParameters.branchName ? serviceRequestParameters.branchName : '-'}`}</span>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__DetailsRow AssistenciaTecnicaSummary__PropriedadeRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__DetailColumn AssistenciaTecnicaSummary__PropriedadeColumn"
                    >
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA_MUNICIPIO')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA_MODELO')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA_MOTIVO_SOLICITACAO')}
                      {userType === 'COOPERCITRUS' && renderField('POS_VENDA_ASSISTENCIA_TECNICA_TIPO_ORDEM')}

                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA-VALOR_UNITARIO')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA-VALOR_TOTAL')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA-CONDICAO_PAGAMENTO')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA-FORMA_PAGAMENTO')}
                      {renderField('POS_VENDA_ASSISTENCIA_TECNICA-OBSERVACOES')}
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__PhoneRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__PhoneColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="AssistenciaTecnicaSummary__PhoneTitle"
                      >
                        Telefone para contato
                      </Typography.Title>
                      <Form.Item
                        name="TelInput"
                        className="AssistenciaTecnicaSummary__PhoneFormItem"
                        validateTrigger={['onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Por favor, insira um telefone.',
                          },
                          {
                            min: 14,
                            message: 'Telefone inválido',
                          },
                        ]}
                      >
                        <MaskedInput
                          guide={false}
                          mask={mask}
                          placeholder="Telefone"
                          className="ant-input AssistenciaTecnicaSummary__PhoneMaskedInput"
                          maxLength={15}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__CommentsRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__CommentsColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="AssistenciaTecnicaSummary__CommentsTitle"
                      >
                        Observação
                      </Typography.Title>
                      <Form.Item
                        className="AssistenciaTecnicaSummary__CommentsFormItem"
                        name="observacao"
                        rules={[
                          {
                            max: 1000,
                            message: 'A observação deve ter no máximo 1000 caractéres',
                          },
                        ]}
                      >
                        <Input.TextArea
                          className="obsArea AssistenciaTecnicaSummary__CommentsInput"
                          xs={{ span: 24 }}
                          lg={{ span: 10 }}
                          rows={4}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    gutter={[8, 8]}
                    className="AssistenciaTecnicaSummary__AttachmentsRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      className="AssistenciaTecnicaSummary__AttachmentsColumn"
                    >
                      <Typography.Title
                        level={4}
                        className="AssistenciaTecnicaSummary__AttachmentsTitle"
                      >
                        Imagens do equipamento
                      </Typography.Title>
                      <Form.Item
                        name="anexo"
                        className="AssistenciaTecnicaSummary__AttachmentsFormItem"
                      >
                        <div className="attachments-items-container">
                          <UploadFiles
                            ref={uploadFilesReference}
                            origin="summary"
                            requestType={
                              isItEstimateOrService(
                                serviceRequestParameters.choiceServiceType === 1 ? 'ORCAMENTO' : 'SERVICO',
                                userType,
                              )
                            }
                            typeUser="SERVICE_REQUEST_CUSTOMER_FILE"
                            action="edit"
                            id={requestId}
                            fileSize={valueAttachmentSizeCooperated}
                            initialFiles={
                              [].map((item) => ({ ...item, uid: item.id, status: 'done' }))
                            }
                          />
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 12 }}
                  className="AssistenciaTecnicaSummary__GeomapColumn"
                >
                </Col>
              </Row>

              <Row
                gutter={[24, 24]}
                justify="end"
                className="AssistenciaTecnicaSummary__ButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  className="AssistenciaTecnicaSummary__ButtonFirstColumn"
                >
                  <Button
                    block
                    className="AssistenciaTecnicaSummary__BackButton"
                    onClick={
                      (): void => {
                        previousPage();
                      }
                    }
                    loading={loading}
                  >
                    <ArrowLeftIcon className="AssistenciaTecnicaSummary__ArrowLeftIcon" />
                    Voltar
                  </Button>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  className="AssistenciaTecnicaSummary__ButtonSecondColumn"
                >
                  <Form.Item
                    className="AssistenciaTecnicaSummary__ContinueButtonFormItem"
                  >
                    <Button
                      block
                      type="primary"
                      htmlType="submit"
                      className="AssistenciaTecnicaSummary__ContinueButton"
                      loading={loading}
                    >
                      Confirmar Solicitação
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

Summary.propTypes = {
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,

  serviceRequestParameters: PropTypes.shape({
    haveCtc: PropTypes.bool,
    ctc: PropTypes.string,
    branchCoopercitrusId: PropTypes.number,
    choiceServiceType: PropTypes.number,
    branchName: PropTypes.string,

    serviceTypeDynamicFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        idCode: PropTypes.string,
      }),
    ).isRequired,

    serviceSubtypeDynamicFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        idCode: PropTypes.string,
      }),
    ).isRequired,

    service: PropTypes.string,

    serviceRequestFieldInputList: PropTypes.arrayOf(
      PropTypes.shape({
        idCode: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.instanceOf(moment),
          PropTypes.bool,
        ]),
      }),
    ),

    selectedProperty: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      registeredOn: PropTypes.instanceOf(moment),
      matriculation: PropTypes.string,

      location: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
    }),

    selectedType: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    selectedService: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    selectedOwner: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    }),

    talhoesSelecionados: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        area: PropTypes.number,
        culture: PropTypes.shape({}),
        details: PropTypes.string,
        landId: PropTypes.number,
        name: PropTypes.string,

        polygon: PropTypes.arrayOf(
          PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
          }),
        ),
      }),
    ),
  }).isRequired,
};
Summary.defaultProps = {};

export default Summary;
